import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { INPUT_TYPES, type Bereich } from "@jornado/shared";
import { api, fehlertext } from "./api";
import { useBereiche, type Bereichsangabe } from "./bereiche";
import { Berichtsvorlagen } from "./berichtsvorlagen";
import { Auswahl, Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Seitenkopf, Textknopf } from "./ui";

/**
 * Der Checklisten-Baukasten.
 *
 * Die Bereiche, darunter unmittelbar die Pruefpunkte. Mehr gibt es nicht,
 * und das ist der Sinn der Sache: bis zum 24.08.2026 standen hier drei
 * abstrakte Listen (Module, Punkte, Vorlagen), von denen keine die Frage
 * beantwortet, was an einem Haus geprueft wird.
 *
 * Seit dem 03.09.2026 werden auch die Bereiche hier gepflegt. Vorher waren es
 * vier feste, Immobilie, Pool, Whirlpool, Garten, und damit war der Bericht
 * einer Hausbetreuung auf Mallorca fest verdrahtet. Wer seinen Bericht selbst
 * gestalten soll, bestimmt auch die Ueberschriften, unter denen er steht. Eine
 * neue Anlage startet deshalb leer, und der erste Schritt beim Einrichten ist
 * ein Bereich.
 *
 * Die Zwischenueberschrift ist ein Feld am Punkt und kein eigenes Gebilde.
 * Sichtbar wird sie dort, wo sie sich gegenueber dem Punkt darueber aendert.
 * Damit bestimmt die Reihenfolge die Gliederung, und es gibt keine zweite
 * Stelle, an der jemand Gruppen pflegen muesste. Damit sie sich trotzdem
 * schnell tippt, schlaegt das Feld die Ueberschriften vor, die es im Bereich
 * schon gibt: einmal schreiben, danach auswaehlen.
 */

type I18nText = Record<string, string>;

/** Ein Startbestand, wie ihn der Server anbietet. Nur die Kennzahlen, nicht der Inhalt. */
interface Vorlage {
  key: string;
  name: I18nText;
  beschreibung: I18nText;
  bereiche: number;
  punkte: number;
}

export interface Pruefpunkt {
  id: string;
  key: string;
  bereich: Bereich;
  label_i18n: I18nText;
  help_i18n: I18nText;
  gruppe_i18n: I18nText;
  input_type: string;
  unit: string | null;
  target_min: string | null;
  target_max: string | null;
  photo_required: boolean;
  sort_order: number;
  is_active: boolean;
}

/** Beschriftung in der Sprache des Lesers, sonst Deutsch, sonst der Ersatz. */
export function beschriftung(text: I18nText | null | undefined, sprache: string, ersatz = ""): string {
  if (!text) return ersatz;
  return text[sprache] || text.de || text.en || Object.values(text)[0] || ersatz;
}

/** "7,0 bis 7,4 mg/l" oder nichts, wenn kein Zielbereich hinterlegt ist. */
export function zielbereich(min: string | null, max: string | null, einheit: string | null): string | null {
  if (min === null && max === null) return null;
  const zahl = (w: string | null) => (w === null ? "" : String(Number(w)));
  const bereich = min !== null && max !== null ? `${zahl(min)} bis ${zahl(max)}`
    : min !== null ? `ab ${zahl(min)}` : `bis ${zahl(max)}`;
  return einheit ? `${bereich} ${einheit}` : bereich;
}

export default function BaukastenSeite({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation();
  const { bereiche, neuLaden } = useBereiche();
  const [punkte, setPunkte] = useState<Pruefpunkt[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuerBereich, setNeuerBereich] = useState(false);

  function laden() {
    void api.get<{ checkpoints: Pruefpunkt[] }>("/checkpoints?alle=ja")
      .then((r) => { setPunkte(r.checkpoints); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, []);

  /** Bereiche und Punkte haengen zusammen, also wird beides zusammen geholt. */
  async function allesNeu() {
    await neuLaden();
    laden();
  }

  return (
    <>
      <Seitenkopf
        titel={t("baukasten.titel")}
        hinweis={t("baukasten.hinweis")}
        aktionen={darfAendern && bereiche?.length ? (
          <Knopf art="neben" onClick={() => setNeuerBereich(true)}>{t("baukasten.bereichNeu")}</Knopf>
        ) : undefined}
      />
      <Fehler text={fehler} />

      {(neuerBereich || (bereiche?.length === 0 && darfAendern)) && (
        <Karte className="mb-6">
          <BereichFormular
            bereich={null}
            onFertig={() => { setNeuerBereich(false); void allesNeu(); }}
            onAbbruch={bereiche?.length ? () => setNeuerBereich(false) : undefined}
          />
        </Karte>
      )}

      {/*
        * Der Startbestand nur beim Einrichten.
        *
        * Solange kein einziger Pruefpunkt steht, ist die Anlage neu, und ein
        * Knopf, der in zwei Sekunden achtzehn Punkte hinstellt, spart die erste
        * Stunde. Steht der erste Punkt, ist Einrichten vorbei: ein zweiter
        * Bestand quer in eine gewachsene Liste waere Unordnung und keine Hilfe.
        */}
      {darfAendern && punkte?.length === 0 && (
        <Vorlagenkarte onGeladen={() => void allesNeu()} onFehler={setFehler} />
      )}

      {!punkte || !bereiche ? <Laedt /> : bereiche.length === 0 ? (
        /*
         * Der leere Anfang ist der Regelfall und kein Fehler: eine frisch
         * eingerichtete Anlage bringt nichts mit, weil ein vorgegebener Bestand
         * immer der Bestand eines fremden Betriebs waere.
         */
        <Karte>
          <p className="text-sm text-leise">{t("baukasten.keineBereiche")}</p>
        </Karte>
      ) : (
        <div className="space-y-6">
          {bereiche.map((b, i) => (
            <Bereichsblock
              key={b.id}
              bereich={b}
              erster={i === 0}
              letzter={i === bereiche.length - 1}
              punkte={punkte.filter((p) => p.bereich === b.key)}
              darfAendern={darfAendern}
              onVerschieben={async (richtung) => {
                const ziel = i + richtung;
                if (ziel < 0 || ziel >= bereiche.length) return;
                const ids = bereiche.map((x) => x.id);
                [ids[i], ids[ziel]] = [ids[ziel], ids[i]];
                try {
                  await api.post("/bereiche/reihenfolge", { ids });
                  await allesNeu();
                } catch (e) {
                  setFehler(fehlertext(e, t("fehler.speichern")));
                }
              }}
              onGeaendert={() => void allesNeu()}
              onFehler={setFehler}
            />
          ))}

          {/*
            * Die Berichtsvorlagen stehen unter den Bereichen und nicht
            * darueber: sie setzen sie voraus. Wer noch keinen Bereich hat,
            * sieht hier nur die Aufforderung, erst einen anzulegen.
            */}
          <Berichtsvorlagen darfAendern={darfAendern} />
        </div>
      )}
    </>
  );
}

function Bereichsblock({
  bereich, punkte, erster, letzter, darfAendern, onVerschieben, onGeaendert, onFehler
}: {
  bereich: Bereichsangabe;
  punkte: Pruefpunkt[];
  erster: boolean;
  letzter: boolean;
  darfAendern: boolean;
  onVerschieben: (richtung: -1 | 1) => void;
  onGeaendert: () => void;
  onFehler: (text: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const [neu, setNeu] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<Pruefpunkt | null>(null);
  const [umbenennen, setUmbenennen] = useState(false);

  /*
   * Die Zwischenueberschriften, die es in diesem Bereich schon gibt, in ihrer
   * Reihenfolge. Sie stehen im Formular als Vorschlagsliste. Ein Abschnitt
   * entsteht dadurch, dass mehrere Punkte hintereinander dieselbe Ueberschrift
   * tragen, und was man von Hand abtippt, tippt man beim vierten Punkt anders.
   * Deutsch, denn deutsch wird getippt; die anderen Sprachen schreibt Claude.
   */
  const ueberschriften = [...new Set(
    punkte.map((p) => beschriftung(p.gruppe_i18n, "de")).filter((x) => x !== "")
  )];

  /*
   * Verschieben aendert die Reihenfolge des ganzen Bereichs in einem Zug.
   *
   * Ein Aufruf je Punkt waere bei dreissig Punkten dreissig Anfragen, und
   * zwischendrin traegen zwei Punkte dieselbe Nummer.
   */
  async function verschieben(index: number, richtung: -1 | 1) {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= punkte.length) return;
    const ids = punkte.map((p) => p.id);
    [ids[index], ids[ziel]] = [ids[ziel], ids[index]];
    try {
      await api.post("/checkpoints/reihenfolge", { bereich: bereich.key, ids });
      onGeaendert();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function bereichStilllegen() {
    try {
      await api.patch(`/bereiche/${bereich.id}`, { isActive: !bereich.is_active });
      onGeaendert();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  /*
   * Geloescht wird nur ein leerer Bereich. Der Server sagt dasselbe noch
   * einmal, denn der Knopf ist nicht die Stelle, an der Regeln gelten.
   */
  async function bereichLoeschen() {
    try {
      await api.del(`/bereiche/${bereich.id}`);
      onGeaendert();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function stilllegen(p: Pruefpunkt) {
    try {
      await api.patch(`/checkpoints/${p.id}`, { isActive: !p.is_active });
      onGeaendert();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  /*
   * Die Fotopflicht haengt am Punkt und wird oft geaendert, deshalb steht sie
   * als Haken in der Zeile und nicht erst im Formular. Wer zwanzig Punkte
   * durchgeht, soll nicht zwanzig Mal ein Formular oeffnen und schliessen.
   */
  async function fotoSchalten(p: Pruefpunkt, pflicht: boolean) {
    try {
      await api.patch(`/checkpoints/${p.id}`, { photoRequired: pflicht });
      onGeaendert();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className={`text-lg font-bold ${bereich.is_active ? "" : "text-leise line-through"}`}>
          {beschriftung(bereich.name_i18n, i18n.language, bereich.key)}
        </h2>
        <span className="text-sm text-leise">
          {t("baukasten.punkteAnzahl", { count: punkte.filter((p) => p.is_active).length })}
        </span>
        {!bereich.is_active && (
          <span className="rounded bg-leise/20 px-2 py-0.5 text-xs">{t("baukasten.stillgelegt")}</span>
        )}
        {darfAendern && (
          <span className="flex flex-wrap items-center gap-2">
            <Textknopf onClick={() => onVerschieben(-1)} disabled={erster}
              aria-label={t("baukasten.hoch")}>↑</Textknopf>
            <Textknopf onClick={() => onVerschieben(1)} disabled={letzter}
              aria-label={t("baukasten.runter")}>↓</Textknopf>
            <Textknopf onClick={() => setUmbenennen(true)}>{t("baukasten.bereichUmbenennen")}</Textknopf>
            <Textknopf onClick={bereichStilllegen}>
              {bereich.is_active ? t("baukasten.stilllegen") : t("baukasten.wiederAktiv")}
            </Textknopf>
            {punkte.length === 0 && (
              <Textknopf gefahr onClick={bereichLoeschen}>{t("aktion.loeschen")}</Textknopf>
            )}
            {!neu && (
              <Knopf art="neben" onClick={() => { setBearbeitet(null); setNeu(true); }}>
                {t("baukasten.punktNeu")}
              </Knopf>
            )}
          </span>
        )}
      </div>

      {umbenennen && (
        <BereichFormular
          bereich={bereich}
          onFertig={() => { setUmbenennen(false); onGeaendert(); }}
          onAbbruch={() => setUmbenennen(false)}
        />
      )}

      {(neu || bearbeitet) && (
        <PunktFormular
          bereich={bereich.key}
          punkt={bearbeitet}
          ueberschriften={ueberschriften}
          onFertig={() => { setNeu(false); setBearbeitet(null); onGeaendert(); }}
          onAbbruch={() => { setNeu(false); setBearbeitet(null); }}
        />
      )}

      {punkte.length === 0 ? (
        <p className="text-sm text-leise">{t("baukasten.keinePunkte")}</p>
      ) : (
        <ul className="divide-y divide-linie">
          {punkte.map((p, i) => {
            const ueberschrift = beschriftung(p.gruppe_i18n, i18n.language);
            const davor = i > 0 ? beschriftung(punkte[i - 1].gruppe_i18n, i18n.language) : null;
            return (
              <li key={p.id}>
                {ueberschrift && ueberschrift !== davor && (
                  <div className="pt-3 text-xs font-semibold uppercase tracking-wide text-leise">
                    {ueberschrift}
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 py-2">
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className={`text-sm font-medium ${p.is_active ? "" : "text-leise line-through"}`}>
                      {beschriftung(p.label_i18n, i18n.language, p.key)}
                    </span>
                    {/* Bei "In Ordnung, Mangel, entfaellt" traegt die Angabe nichts
                        bei, das ist der Regelfall. Nur Abweichungen werden benannt. */}
                    {p.input_type !== "status" && (
                      <span className="text-xs text-leise">{t(`baukasten.art.${p.input_type}`)}</span>
                    )}
                    {zielbereich(p.target_min, p.target_max, p.unit) && (
                      <span className="rounded bg-grund px-2 py-0.5 text-xs">
                        {zielbereich(p.target_min, p.target_max, p.unit)}
                      </span>
                    )}
                    {darfAendern ? (
                      <label className="flex items-center gap-1.5 rounded bg-grund px-2 py-0.5 text-xs">
                        <input
                          type="checkbox"
                          className="size-4 accent-marke"
                          checked={p.photo_required}
                          onChange={(e) => fotoSchalten(p, e.target.checked)}
                        />
                        {t("baukasten.fotoPflicht")}
                      </label>
                    ) : p.photo_required && (
                      <span className="rounded bg-grund px-2 py-0.5 text-xs">{t("baukasten.fotoPflicht")}</span>
                    )}
                    {!p.is_active && (
                      <span className="rounded bg-leise/20 px-2 py-0.5 text-xs">{t("baukasten.stillgelegt")}</span>
                    )}
                  </span>
                  {darfAendern && (
                    <span className="flex shrink-0 items-center gap-2">
                      <Textknopf onClick={() => verschieben(i, -1)} disabled={i === 0}
                        aria-label={t("baukasten.hoch")}>↑</Textknopf>
                      <Textknopf onClick={() => verschieben(i, 1)} disabled={i === punkte.length - 1}
                        aria-label={t("baukasten.runter")}>↓</Textknopf>
                      <Textknopf onClick={() => { setNeu(false); setBearbeitet(p); }}>
                        {t("aktion.bearbeiten")}
                      </Textknopf>
                      <Textknopf gefahr={p.is_active} onClick={() => stilllegen(p)}>
                        {p.is_active ? t("baukasten.stilllegen") : t("baukasten.wiederAktiv")}
                      </Textknopf>
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Karte>
  );
}

/**
 * Anlegen und Aendern in einem Formular.
 *
 * Der Schluessel laesst sich nachtraeglich nicht mehr aendern: an ihm haengen
 * bereits geschriebene Berichte.
 */
function PunktFormular({
  bereich, punkt, ueberschriften, onFertig, onAbbruch
}: {
  bereich: Bereich;
  punkt: Pruefpunkt | null;
  /** Was im Bereich schon als Zwischenueberschrift steht, zur Auswahl. */
  ueberschriften: string[];
  onFertig: () => void;
  onAbbruch: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [label, setLabel] = useState(beschriftung(punkt?.label_i18n, "de"));
  const [gruppe, setGruppe] = useState(beschriftung(punkt?.gruppe_i18n, "de"));
  const [hilfe, setHilfe] = useState(beschriftung(punkt?.help_i18n, "de"));
  const [art, setArt] = useState(punkt?.input_type ?? "status");
  const [einheit, setEinheit] = useState(punkt?.unit ?? "");
  const [min, setMin] = useState(punkt?.target_min ? String(Number(punkt.target_min)) : "");
  const [max, setMax] = useState(punkt?.target_max ? String(Number(punkt.target_max)) : "");
  const [foto, setFoto] = useState(punkt?.photo_required ?? false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    /*
     * Deutsch tippen reicht. Englisch, Spanisch und Arabisch schreibt Claude
     * beim Speichern, siehe uebersetzen.ts im Server. Von Hand korrigierte
     * Sprachen bleiben dabei stehen.
     */
    const daten = {
      bereich,
      labelI18n: { de: label.trim() },
      gruppeI18n: gruppe.trim() ? { de: gruppe.trim() } : undefined,
      helpI18n: hilfe.trim() ? { de: hilfe.trim() } : undefined,
      inputType: art,
      unit: einheit.trim() || undefined,
      targetMin: art === "number" ? min : "",
      targetMax: art === "number" ? max : "",
      photoRequired: foto
    };
    try {
      // Ohne Schluessel: den bildet der Server aus der Beschriftung.
      if (punkt) await api.patch(`/checkpoints/${punkt.id}`, daten);
      else await api.post("/checkpoints", daten);
      onFertig();
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden} className="mb-4 rounded border border-marke/40 bg-grund/40 p-4">
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("baukasten.beschriftung")} hinweis={t("baukasten.uebersetztHinweis")} breit>
          <Eingabe required value={label} onChange={(e) => setLabel(e.target.value)} />
        </Feld>
        <Feld label={t("baukasten.gruppe")} hinweis={t("baukasten.gruppeHinweis")}>
          {/*
            * Eingeben oder auswaehlen, beides im selben Feld. Eine reine
            * Auswahlliste liesse die erste Ueberschrift nicht zu, ein reines
            * Textfeld erzeugt beim vierten Punkt "Aussen " mit Leerzeichen.
            * Die Vorschlagsliste des Browsers kann beides und braucht keinen
            * zweiten Knopf daneben.
            */}
          <Eingabe
            list={`ueberschriften-${bereich}`}
            value={gruppe}
            onChange={(e) => setGruppe(e.target.value)}
            placeholder={t("baukasten.gruppeBeispiel")}
          />
          <datalist id={`ueberschriften-${bereich}`}>
            {ueberschriften.map((u) => <option key={u} value={u} />)}
          </datalist>
        </Feld>
        <Feld label={t("baukasten.eingabeart")} hinweis={t("baukasten.eingabeartHinweis")}>
          <Auswahl value={art} onChange={(e) => setArt(e.target.value)}>
            {INPUT_TYPES.map((a) => <option key={a} value={a}>{t(`baukasten.art.${a}`)}</option>)}
          </Auswahl>
        </Feld>
        {art === "number" && (
          <>
            <Feld label={t("baukasten.einheit")}>
              <Eingabe value={einheit} onChange={(e) => setEinheit(e.target.value)} placeholder="mg/l" />
            </Feld>
            <Feld label={t("baukasten.zielVon")} hinweis={t("baukasten.zielHinweis")}>
              <Eingabe inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
            </Feld>
            <Feld label={t("baukasten.zielBis")}>
              <Eingabe inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
            </Feld>
          </>
        )}
        <Feld label={t("baukasten.hilfetext")} hinweis={t("baukasten.hilfetextHinweis")} breit>
          <Eingabe value={hilfe} onChange={(e) => setHilfe(e.target.value)} />
        </Feld>
      </div>

      <label className="mt-3 flex items-center gap-3 text-sm">
        <input type="checkbox" className="size-5 accent-marke" checked={foto}
          onChange={(e) => setFoto(e.target.checked)} />
        <span>
          <span className="font-medium">{t("baukasten.fotoPflicht")}</span>
          <span className="block text-xs text-leise">{t("baukasten.fotoPflichtLang")}</span>
        </span>
      </label>

      {punkt && (
        <p className="mt-3 text-xs text-leise">
          {t("baukasten.schluessel")}: {punkt.key}
          {beschriftung(punkt.label_i18n, i18n.language) !== beschriftung(punkt.label_i18n, "de") && (
            <> · {beschriftung(punkt.label_i18n, i18n.language)}</>
          )}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Knopf type="submit" disabled={laeuft}>{laeuft ? t("aktion.speichert") : t("aktion.speichern")}</Knopf>
        <Knopf type="button" art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </form>
  );
}

/**
 * Der Startbestand zum Laden.
 *
 * Er nimmt dem neuen Kunden die erste Stunde ab, in der sonst vierzig Punkte
 * von Hand entstehen, ohne ihm etwas vorzuschreiben: geladen wird auf Druck,
 * und danach gehoert jeder Punkt ihm. Umbenennen, ergaenzen, loeschen.
 *
 * Zwei Vorlagen decken wiederkehrende Rundgaenge ab, eine den einmaligen
 * Auftrag. Wer nichts davon braucht, faengt mit einem eigenen Bereich an; die
 * Karte steht deshalb unter dem Anlegen und nicht darueber.
 */
function Vorlagenkarte({
  onGeladen, onFehler
}: {
  onGeladen: () => void;
  onFehler: (text: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const [vorlagen, setVorlagen] = useState<Vorlage[] | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ vorlagen: Vorlage[] }>("/baukasten/vorlagen")
      .then((r) => setVorlagen(r.vorlagen))
      // Ohne Vorlagen geht das Einrichten von Hand weiter. Ein Fehlerstreifen
      // ueber einer Bequemlichkeit waere lauter als die Sache wert ist.
      .catch(() => setVorlagen([]));
  }, []);

  async function laden(v: Vorlage) {
    setLaeuft(v.key);
    onFehler(null);
    try {
      await api.post(`/baukasten/vorlagen/${v.key}`, {});
      onGeladen();
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(null);
    }
  }

  if (!vorlagen || vorlagen.length === 0) return null;

  return (
    <Karte className="mb-6">
      <h2 className="mb-1 font-bold">{t("baukasten.vorlagenTitel")}</h2>
      <p className="mb-4 text-sm text-leise">{t("baukasten.vorlagenHinweis")}</p>
      <ul className="space-y-3">
        {vorlagen.map((v) => (
          <li key={v.key} className="flex flex-wrap items-center justify-between gap-3 border-t border-linie pt-3 first:border-0 first:pt-0">
            <span className="min-w-0">
              <span className="block font-medium">{beschriftung(v.name, i18n.language)}</span>
              <span className="block text-sm text-leise">{beschriftung(v.beschreibung, i18n.language)}</span>
              <span className="block text-xs text-leise">
                {t("baukasten.vorlagenUmfang", { bereiche: v.bereiche, punkte: v.punkte })}
              </span>
            </span>
            <Knopf art="neben" disabled={laeuft !== null} onClick={() => laden(v)}>
              {laeuft === v.key ? t("aktion.speichert") : t("baukasten.vorlageLaden")}
            </Knopf>
          </li>
        ))}
      </ul>
    </Karte>
  );
}

/**
 * Einen Bereich anlegen oder umbenennen.
 *
 * Nur ein Feld, und das ist Absicht. Der Schluessel wird aus dem Namen
 * gebildet und bleibt danach fest, weil Pruefpunkte daran haengen; die
 * Reihenfolge wird mit den Pfeilen gesetzt, nicht mit einer Zahl.
 */
function BereichFormular({
  bereich, onFertig, onAbbruch
}: {
  bereich: Bereichsangabe | null;
  onFertig: () => void;
  /** Fehlt beim ersten Bereich einer neuen Anlage: dort gibt es nichts, wohin abgebrochen wird. */
  onAbbruch?: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(beschriftung(bereich?.name_i18n, "de"));
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      // Deutsch tippen reicht, den Rest schreibt Claude beim Speichern.
      if (bereich) await api.patch(`/bereiche/${bereich.id}`, { nameI18n: { de: name.trim() } });
      else await api.post("/bereiche", { nameI18n: { de: name.trim() } });
      onFertig();
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden} className="mb-4 rounded border border-marke/40 bg-grund/40 p-4">
      <Fehler text={fehler} />
      <Feld label={t("baukasten.bereichName")} hinweis={t("baukasten.bereichNameHinweis")} breit>
        <Eingabe required autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder={t("baukasten.bereichBeispiel")} />
      </Feld>
      <div className="mt-4 flex gap-2">
        <Knopf type="submit" disabled={laeuft}>{laeuft ? t("aktion.speichert") : t("aktion.speichern")}</Knopf>
        {onAbbruch && (
          <Knopf type="button" art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
        )}
      </div>
    </form>
  );
}

export function Abschnitt({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-lg font-bold">{titel}</h2>
      {children}
    </section>
  );
}
